/**
 * Permission codes the Business Units screens (FSD 1.2.4) declare an interest in.
 *
 * These are the dot-strings `server/internal/org/businessunit.go` registers
 * with the RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. The
 * wire path is `/api/v1/foundation/business-units` (FSD's own naming) and,
 * unlike Legal Entities (`DECISIONS.md` 2026-09-19), the permission strings
 * themselves DO say `foundation.business_unit.*` — verified directly against
 * `businessunit.go`'s `PermBU*` constants and `handler_bu.go`'s route table,
 * not guessed from the URL. The audit entity these screens' right rail cites
 * is also the literal string `foundation.business_unit`
 * (`docTypeBusinessUnit`, `businessunit.go`), again unlike Legal Entities.
 *
 * Confirmed per route (`handler_bu.go`):
 *  - create / activate / deactivate / assign-reference-sets → `manage`
 *  - get / resolve-client / list-ledger-alignments          → `read`
 *  - assign / remove business function                      → `function_assign`
 *  - service-provider relationship create                   → `service_provider`
 *  - security-scope preview                                 → `security_scope`
 *  - align-ledger (write)                                    → `doc_sequence`
 *    (the READ side of ledger alignment — the list route — is gated on
 *    `read`, not `doc_sequence`: the two are NOT a symmetric pair, matched
 *    exactly against the handler rather than assumed.)
 *
 * There is no `update` or `submit` permission because the live route table
 * has no `PUT`/`PATCH` business-unit route and no submit-for-approval route
 * (`handler_bu.go` lists exactly 13 routes; FSD 1.2.4.5's "Submit for
 * Approval" action is not yet wired to a live endpoint). This module does not
 * fabricate a screen for either — see `BusinessUnitView.vue`'s own header
 * comment.
 */

export const BUSINESS_UNIT_PERMISSIONS = {
  read: 'foundation.business_unit.read',
  manage: 'foundation.business_unit.manage',
  functionAssign: 'foundation.business_unit.function_assign',
  serviceProvider: 'foundation.business_unit.service_provider',
  securityScope: 'foundation.business_unit.security_scope',
  docSequence: 'foundation.business_unit.doc_sequence',
} as const

export type BusinessUnitPermission = (typeof BUSINESS_UNIT_PERMISSIONS)[keyof typeof BUSINESS_UNIT_PERMISSIONS]
